/**
 * useAdminEditMode
 *
 * Powers the public-site "Edit Mode" feature: a logged-in Master Admin can
 * append `?edit=yes` to any public page URL (or flip the toggle this hook
 * backs) to turn every module on the page into a clickable "edit this"
 * target. `isAdmin` is resolved via the existing `/auth/me` session check
 * (same cookie the admin dashboard itself uses) — a visitor without that
 * cookie can never see edit mode no matter what's in the URL.
 *
 * The on/off flag is mirrored into sessionStorage so it survives clicking
 * between pages — `PageShell`/`ModularPage` remount on every route change
 * (different page component), which would otherwise reset a plain
 * `useState` back to whatever the new URL happens to say. Once on, the
 * flag re-appends `?edit=yes` to the URL on every navigation too, purely
 * so the indicator stays visible/shareable.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { getAuthMe } from "@/utils/chromeCache";

const API = getApiUrl();
const EDIT_MODE_KEY = "slh_edit_mode";
// How long a Master Admin can go without touching the page before the
// "Viewing as Master Admin" / "Edit Mode" banner hides itself. Being
// signed in is not the same as actively using the admin tools — a long
// idle stretch on a public page should stop showing admin-only chrome
// until they interact again.
const IDLE_HIDE_MS = 10 * 60 * 1000;
// How often to re-verify the Master Admin session while Edit Mode stays on
// and the admin never navigates or switches tabs (the two other recheck
// triggers below). Catches session expiry / a logout from another tab or
// device within a bounded window instead of only on the next click.
const SESSION_RECHECK_MS = 60 * 1000;

const readPersistedEditOn = () => {
  try {
    // Once a decision has been made (on OR explicitly off), sessionStorage
    // wins — a stale `?edit=yes` left over in the URL (e.g. the browser
    // back button landing on an old history entry) must not resurrect
    // Edit Mode after the admin turned it off.
    const stored = sessionStorage.getItem(EDIT_MODE_KEY);
    if (stored !== null) return stored === "1";
    return new URLSearchParams(window.location.search).get("edit") === "yes";
  } catch {
    return new URLSearchParams(window.location.search).get("edit") === "yes";
  }
};

export default function useAdminEditMode() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checked, setChecked] = useState(false);
  const [editOn, setEditOn] = useState(readPersistedEditOn);

  // Fully deactivates Edit Mode — not just hides it — so it's never still
  // active after a sign-out. Clears the persisted flag entirely (rather
  // than forcing it to "0") so a *future* admin login in the same tab can
  // still turn it back on via `?edit=yes` — forcing "0" here would permanently
  // shadow the URL param for the rest of the tab's session (readPersistedEditOn
  // prefers storage over the URL), even after logging back in. Also strips
  // a stale `?edit=yes` from the current URL.
  const deactivateEditMode = useCallback(() => {
    try { sessionStorage.removeItem(EDIT_MODE_KEY); } catch { /* storage unavailable */ }
    setEditOn(false);
    const params = new URLSearchParams(window.location.search);
    if (params.get("edit") === "yes") {
      params.delete("edit");
      const qs = params.toString();
      navigate({ pathname: window.location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
    }
  }, [navigate]);

  // Re-verifies the Master Admin session: on first mount, on every route
  // change (covers the common case — logging out on /admin navigates back
  // to a public page), when this tab regains focus/visibility (covers
  // signing out in a different tab), and immediately on the explicit
  // `slh:admin-signed-out` event the logout button fires (no need to wait
  // for a network round trip in that case).
  useEffect(() => {
    let alive = true;
    getAuthMe()
      .then((data) => {
        if (!alive) return;
        const stillAdmin = data?.user_id === "master";
        setIsAdmin(stillAdmin);
        if (!stillAdmin) deactivateEditMode();
      })
      .catch(() => {
        if (!alive) return;
        setIsAdmin(false);
        deactivateEditMode();
      })
      .finally(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const recheckSession = () => {
      axios
        .get(`${API}/auth/me`, { withCredentials: true })
        .then((res) => {
          const stillAdmin = res.data?.user_id === "master";
          setIsAdmin(stillAdmin);
          if (!stillAdmin) deactivateEditMode();
        })
        .catch(() => { setIsAdmin(false); deactivateEditMode(); });
    };
    const onSignedOut = () => {
      setIsAdmin(false);
      deactivateEditMode();
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      recheckSession();
    };
    window.addEventListener("slh:admin-signed-out", onSignedOut);
    document.addEventListener("visibilitychange", onVisible);
    // Also poll periodically while Edit Mode stays on, on the SAME page
    // with no tab switch or navigation — e.g. the session simply expired,
    // or the admin was logged out in another tab/device without that tab
    // ever losing focus. Without this, the route-change/visibility checks
    // above never fire and edit options would otherwise keep showing
    // until the admin happens to click something.
    const interval = setInterval(recheckSession, SESSION_RECHECK_MS);
    return () => {
      window.removeEventListener("slh:admin-signed-out", onSignedOut);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-append `?edit=yes` on every route change while the flag is on, and
  // (re)persist it to sessionStorage — but only once `isAdmin` is actually
  // confirmed, so an anonymous visit to a `?edit=yes` link never seeds the
  // flag for whoever logs in next in that same tab. Depends on
  // `location.search` too (not just `.pathname`) so clicking a link back
  // to the SAME page — which drops the query string entirely — still
  // re-adds `?edit=yes` instead of silently losing it from the address bar.
  useEffect(() => {
    if (!isAdmin || !editOn) return;
    try { sessionStorage.setItem(EDIT_MODE_KEY, "1"); } catch { /* storage unavailable, degrade to URL-only */ }
    const params = new URLSearchParams(location.search);
    if (params.get("edit") !== "yes") {
      params.set("edit", "yes");
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, isAdmin, editOn]);

  // Idle tracking: any real interaction resets the clock. A plain
  // `setInterval` check (rather than updating state on every mousemove)
  // keeps this cheap on public pages that admins mostly just browse.
  const lastActivityRef = useRef(Date.now());
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
      setIdle((prev) => (prev ? false : prev));
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    events.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current > IDLE_HIDE_MS) setIdle(true);
    }, 30_000);
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, markActive));
      clearInterval(interval);
    };
  }, []);

  const editMode = isAdmin && editOn;
  // What the banner should actually use — confirmed admin session AND not
  // idle. `editMode`/`setEditParam` stay keyed off the raw `isAdmin` so
  // in-progress editing state itself isn't disturbed by an idle banner.
  const bannerVisible = isAdmin && !idle;

  // Flips the persisted flag + `?edit=yes` in the URL without a full
  // page reload.
  const setEditParam = useCallback((on) => {
    try {
      sessionStorage.setItem(EDIT_MODE_KEY, on ? "1" : "0");
    } catch { /* storage unavailable, degrade to URL-only */ }
    setEditOn(on);
    const next = new URLSearchParams(location.search);
    if (on) next.set("edit", "yes");
    else next.delete("edit");
    const qs = next.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return { isAdmin, bannerVisible, checked, editMode, setEditParam };
}
